"use client";

import { useEffect, useRef, useCallback } from "react";
import { authClient } from "@/lib/auth/auth-client";
import {
  isNativePlatform,
  closeInAppBrowser,
  isAuthCallbackUrl,
  isPlatformCallbackUrl,
  extractParamsFromUrl,
} from "@/lib/capacitor/native-auth";
import {
  createErrorReporter,
  reportWarning,
} from "@/lib/observability/report-error";

const report = createErrorReporter("hooks.useNativeAuth", "auth");

/**
 * Global hook that sets up deep link listeners for native OAuth callbacks.
 * Handles both Better Auth auth callbacks and platform connection callbacks.
 *
 * On native platforms (Capacitor iOS/Android), OAuth flows open in an in-app
 * browser (Chrome Custom Tabs / SFSafariViewController), which has a cookie
 * jar isolated from the app's WebView. The auth flow therefore runs entirely
 * inside the in-app browser; on success, /api/auth/native-oauth-bridge
 * redirects Custom Tabs to the app's custom URL scheme with a single-use
 * bridge token. This hook catches that deep link, exchanges the token for a
 * session cookie inside the WebView, and navigates the user appropriately.
 *
 * Should be mounted once at the root layout level via NativeDeepLinkHandler.
 */
export function useNativeAuth() {
  const listenerRef = useRef<{ remove: () => void } | null>(null);
  const isProcessingRef = useRef(false);

  const handleDeepLink = useCallback(async (url: string) => {
    console.log("[useNativeAuth] Deep link received:", url.slice(0, 120));
    if (isProcessingRef.current) return;

    if (isAuthCallbackUrl(url)) {
      isProcessingRef.current = true;
      console.log("[useNativeAuth] Auth callback detected, processing...");
      try {
        await closeInAppBrowser();

        const params = extractParamsFromUrl(url);
        const bridgeToken = params?.token;
        const callbackError = params?.error;

        if (callbackError) {
          console.error(
            "[useNativeAuth] OAuth callback returned error:",
            callbackError
          );
          window.location.href = `/login?error=${encodeURIComponent(
            callbackError
          )}`;
          return;
        }

        if (!bridgeToken) {
          console.error(
            "[useNativeAuth] Auth callback URL missing bridge token and error code"
          );
          window.location.href = "/login?error=auth";
          return;
        }

        console.log(
          "[useNativeAuth] Exchanging bridge token for WebView session..."
        );
        const exchangeResponse = await fetch(
          "/api/auth/native-oauth-exchange/",
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: bridgeToken }),
          }
        );

        if (!exchangeResponse.ok) {
          const errBody = await exchangeResponse
            .json()
            .catch(() => ({ error: `http_${exchangeResponse.status}` }));
          console.error(
            "[useNativeAuth] Bridge token exchange failed:",
            exchangeResponse.status,
            errBody
          );
          reportWarning(
            new Error(
              `Bridge token exchange failed: ${exchangeResponse.status} ${
                (errBody as { error?: string }).error ?? "unknown"
              }`
            ),
            {
              source: "hooks.useNativeAuth",
              feature: "auth",
              action: "exchangeBridgeToken",
              extra: { status: exchangeResponse.status },
            }
          );
          window.location.href = "/login?error=auth";
          return;
        }

        // Trigger server-side user provisioning and identity mapping.
        try {
          const provisionRes = await fetch("/api/auth/provision/", {
            method: "POST",
            credentials: "include",
          });
          if (provisionRes.status === 403) {
            await authClient.signOut();
            window.location.href = "/signup?error=abuse_blocked";
            return;
          }
        } catch (provisionErr) {
          console.error("[useNativeAuth] Provisioning failed:", provisionErr);
          reportWarning(provisionErr, {
            source: "hooks.useNativeAuth",
            feature: "auth",
            action: "provision",
          });
        }

        // Session cookie is now set in the WebView. Full page load so SSR
        // picks up the session.
        window.location.href = "/dashboard";
      } catch (err) {
        console.error("[useNativeAuth] Unexpected error:", err);
        report(err, { action: "handleAuthCallback" });
        window.location.href = "/login?error=auth";
      } finally {
        isProcessingRef.current = false;
      }
    } else if (isPlatformCallbackUrl(url)) {
      isProcessingRef.current = true;
      try {
        await closeInAppBrowser();

        const params = extractParamsFromUrl(url);
        const connected = params?.connected;
        const callbackError = params?.error;

        if (callbackError) {
          window.location.href = `/settings/accounts?error=${encodeURIComponent(callbackError)}`;
        } else if (connected) {
          window.location.href = `/settings/accounts?connected=${encodeURIComponent(connected)}`;
        } else {
          window.location.href = "/settings/accounts";
        }
      } catch (err) {
        console.error("[useNativeAuth] Platform callback error:", err);
        report(err, { action: "handlePlatformCallback" });
        window.location.href = "/settings/accounts";
      } finally {
        isProcessingRef.current = false;
      }
    }
  }, []);

  useEffect(() => {
    if (!isNativePlatform()) return;

    let cancelled = false;

    import("@capacitor/app").then(({ App }) => {
      if (cancelled) return;

      App.addListener("appUrlOpen", (event: { url: string }) => {
        handleDeepLink(event.url);
      }).then((handle) => {
        if (cancelled) {
          handle.remove();
        } else {
          listenerRef.current = handle;
        }
      });
    });

    return () => {
      cancelled = true;
      listenerRef.current?.remove();
      listenerRef.current = null;
    };
  }, [handleDeepLink]);
}
